import React, { createContext, useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);
const originalRefresh = supabase.auth.refreshSession.bind(supabase.auth);
supabase.auth.refreshSession = async (...args) => {
  if (refreshLock) {
    console.log('[Auth Lock] Internal refresh waiting for in-flight');
    return refreshLock;
  }
  console.log('[Auth Lock] Internal refresh starting');
  refreshLock = originalRefresh(...args).finally(() => { refreshLock = null; });
  return refreshLock;
};
const AuthContext = createContext();
let refreshLock = null;

const safeRefresh = async () => {
  if (refreshLock) return refreshLock; // Wait for in-flight
  refreshLock = supabase.auth.refreshSession().finally(() => { refreshLock = null; });
  return refreshLock;
};
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // REMOVED: balance (no longer used in new schema)
  const [membership, setMembership] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [membershipLoaded, setMembershipLoaded] = useState(false);
  const [csrfToken, setCsrfToken] = useState(null);
  const [membershipInitialized, setMembershipInitialized] = useState(false); // NEW: Guard for one-time init
  const [isRegistering, setIsRegistering] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const lastUserIdRef = useRef(null);
  const refreshing = useRef(false);
  const initialized = useRef(false);
  const isCheckingAuth = useRef(false);
  const fetching = useRef(false); // Reused for init guard
  const welcomeEmailTriggeredRef = useRef(null);
  const csrfTokenRef = useRef(null); // FIXED: Add missing ref declaration
  const csrfFetchPromiseRef = useRef(null); // Single-flight guard for CSRF fetch
  const [csrfLoaded, setCsrfLoaded] = useState(false);
  const csrfLoadedRef = useRef(false);
  const refreshCooldownUntilRef = useRef(0);
  const serverSessionSyncRef = useRef({ done: false, inFlight: null });
  // NEW: Ref for membershipInitialized to avoid stale closures in listener
  const membershipInitializedRef = useRef(false);
  // NEW: Ref to track if OAuth flow is complete (for chaining init)
  const oauthCompleteRef = useRef(false);
  const oauthSetSessionInFlightRef = useRef(null);
  const oauthSetSessionInFlightTokenRef = useRef(null);
  const oauthSetSessionSyncedTokenRef = useRef(null);
  const isFallbacking = useRef(false); // Guard fallback loop
  const isSyncingServerSessionRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    csrfTokenRef.current = csrfToken;
  }, [csrfToken]);

  const setRefreshCooldown = useCallback((ms = 15000) => {
    refreshCooldownUntilRef.current = Date.now() + ms;
  }, []);

  const isRefreshCooldown = useCallback(() => {
    return Date.now() < refreshCooldownUntilRef.current;
  }, []);

  // *** ALL useCallback HOOKS DECLARED HERE - BEFORE ANY useEffect ***
  const resetStates = () => {
  setMembership(null);
  // REMOVED: setBalance(null);
  setProfile(null);
  setIsAdmin(false);
  setCsrfToken(null);
  welcomeEmailTriggeredRef.current = null;
  setMembershipLoaded(true); // Set to true for unauth
  setMembershipInitialized(false); // NEW: Reset for next session
  setCsrfLoaded(false);
  csrfLoadedRef.current = false;
  csrfFetchPromiseRef.current = null;
  serverSessionSyncRef.current = { done: false, inFlight: null };
  membershipInitializedRef.current = false; // Sync ref
  lastUserIdRef.current = null;
  oauthCompleteRef.current = false; // NEW: Reset OAuth flag
  oauthSetSessionInFlightRef.current = null;
  oauthSetSessionInFlightTokenRef.current = null;
  oauthSetSessionSyncedTokenRef.current = null;
  isCheckingAuth.current = false; // Unstick Skipping guards
};

  const logout = useCallback(async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/signout`, {
        method: 'POST',
        credentials: 'include',
      });
      await supabase.auth.signOut();
      resetStates();
      window.location.href = '/'; // FIXED: Redirect to WelcomeScreen root (no flash to /signin)
    } catch (err) {
      console.error('Logout error:', err.message);
    }
  }, []);

  const syncServerSession = useCallback(async () => {
    if (isSyncingServerSessionRef.current) {
      return false;
    }
    if (serverSessionSyncRef.current.done) return true;
    if (serverSessionSyncRef.current.inFlight) {
      return serverSessionSyncRef.current.inFlight;
    }
    const run = async () => {
      isSyncingServerSessionRef.current = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token || !session?.refresh_token) {
          return false;
        }
        const setRes = await fetch(`${process.env.REACT_APP_API_URL}/auth/set-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          }),
        });
        if (!setRes.ok) {
          const errText = await setRes.text().catch(() => '');
          console.warn('[AuthFlow] set-session failed:', setRes.status, errText);
          return false;
        }
        serverSessionSyncRef.current.done = true;
        return true;
      } catch (err) {
        console.warn('[AuthFlow] set-session error:', err?.message || err);
        return false;
      } finally {
        serverSessionSyncRef.current.inFlight = null;
        isSyncingServerSessionRef.current = false;
      }
    };
    const inFlight = run();
    serverSessionSyncRef.current.inFlight = inFlight;
    return inFlight;
  }, []);

  const refreshSession = useCallback(async () => {
    if (refreshing.current) return false;
    if (isRefreshCooldown()) {
      console.log('[Auth Lock] Skipping refresh (cooldown active)');
      return true;
    }
    refreshing.current = true;
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, { credentials: 'include' });
        if (!response.ok) throw new Error('Server refresh failed');
        const data = await response.json();
        if (!data.isAuthenticated) {
          logout();
          return false;
        }
      } else {
        serverSessionSyncRef.current.done = false;
        if (!isSyncingServerSessionRef.current) {
          await syncServerSession();
        }
      }
      setRefreshCooldown(15000);
      return true;
    } catch (err) {
      console.error('Refresh error:', err.message);
      logout();
      return false;
    } finally {
      refreshing.current = false;
    }
  }, [logout, isRefreshCooldown, setRefreshCooldown, syncServerSession]);

  const refreshAndSync = useCallback(async () => {
    const refreshed = await safeRefresh();
    if (refreshed?.data?.session) {
      serverSessionSyncRef.current.done = false;
      if (!isSyncingServerSessionRef.current) {
        await syncServerSession();
      }
    }
    return refreshed;
  }, [syncServerSession]);

     const fetchCsrfToken = useCallback(async () => {
    if (!user?.id || isResettingPassword) {
      console.log('[CSRF] Skipping fetchCsrfToken (no user or resetting password)');
      return null;
    }

    if (csrfTokenRef.current && csrfLoadedRef.current) {
      console.log('[CSRF] Using cached token');
      return csrfTokenRef.current;
    }

    if (csrfFetchPromiseRef.current) {
      console.log('[CSRF] Awaiting in-flight CSRF fetch');
      return csrfFetchPromiseRef.current;
    }

    const maxAttempts = 6; // Was 2 – now more robust for Render latency
    let attempt = 0;

    const fetchPromise = (async () => {
      let didSyncSession = false;
      while (attempt < maxAttempts) {
        attempt++;
        try {
          console.log(`[CSRF] fetchCsrfToken attempt ${attempt}/${maxAttempts}`);

          const response = await fetch(`${process.env.REACT_APP_API_URL}/csrf-token`, {
            method: 'GET',
            credentials: 'include',
          });

          if (response.ok) {
            const { csrfToken: newToken } = await response.json();
            setCsrfToken(newToken);
            csrfTokenRef.current = newToken;
            setCsrfLoaded(true);
            csrfLoadedRef.current = true;
            console.log(`[CSRF] token fetched successfully on attempt ${attempt}`);
            return newToken;
          }

          // Handle auth-related errors with optional refresh + retry
          if (response.status === 401 || response.status === 403) {
            console.warn(`[CSRF] ${response.status} on attempt ${attempt}`);

            if (!didSyncSession) {
              const synced = await syncServerSession();
              didSyncSession = true;
              if (synced && attempt < maxAttempts) {
                const delay = 300;
                console.warn(`[CSRF] Server session synced, retrying CSRF in ${delay}ms...`);
                await new Promise((r) => setTimeout(r, delay));
                continue;
              }
            }

            // Try a session refresh once in this loop before giving up
            const refreshed = await refreshSession();
            if (refreshed && attempt < maxAttempts) {
              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
              console.warn(
                `[CSRF] Auth refresh succeeded, retrying CSRF in ${delay}ms...`
              );
              await new Promise((r) => setTimeout(r, delay));
              continue;
            }

            if (attempt < maxAttempts) {
              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
              console.warn(
                `[CSRF] Still unauthorized, retry ${attempt + 1}/${maxAttempts} in ${delay}ms...`
              );
              await new Promise((r) => setTimeout(r, delay));
              continue;
            }
          } else {
            console.error(
              `[CSRF] Non-auth error status ${response.status} on attempt ${attempt}`
            );
          }

          // Break out if not retriable or max attempts reached
          break;
        } catch (error) {
          console.error(
            `[CSRF] attempt ${attempt} failed:`,
            error?.message || error
          );
          if (attempt < maxAttempts) {
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
            console.warn(`[CSRF] Retrying in ${delay}ms...`);
            await new Promise((r) => setTimeout(r, delay));
          }
        }
      }

      console.error('[CSRF] giving up after max attempts – continuing without CSRF token');
      // IMPORTANT: we just return null here, but we DO NOT flip loading flags.
      // Routes are no longer blocked on csrfLoaded, so the UI stays usable.
      return null;
    })();

    csrfFetchPromiseRef.current = fetchPromise;
    try {
      return await fetchPromise;
    } finally {
      csrfFetchPromiseRef.current = null;
    }
  }, [user?.id, isResettingPassword, refreshSession, syncServerSession]);

  const fetchWrapper = useCallback(async (url, options = {}) => {
  // Internal helper so we can control "only retry once" without recursion traps
  const doFetch = async (retryState = { auth: false, csrf: false }) => {
    if (!user) {
      console.warn('fetchWrapper called without user - skipping');
      throw new Error('No user for fetch');
    }

    // Ensure we have a CSRF token for this request
    let currentCsrf = csrfTokenRef.current;
    if (!currentCsrf) {
      currentCsrf = await fetchCsrfToken();
      if (!currentCsrf) {
        throw new Error('No CSRF');
      }
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          'x-csrf-token': currentCsrf,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        const isUnauthorized =
          response.status === 401 ||
          (errorText && (
            errorText.includes('Unauthorized') ||
            errorText.includes('Invalid token')
          ));
        const isInvalidCsrf =
          response.status === 403 &&
          errorText &&
          errorText.includes('Invalid CSRF token');

        if (isInvalidCsrf && !retryState.csrf) {
          console.warn('[CSRF] Invalid token, refetching and retrying once.');
          setCsrfToken(null);
          csrfTokenRef.current = null;
          setCsrfLoaded(false);
          csrfLoadedRef.current = false;
          await fetchCsrfToken();
          return doFetch({ ...retryState, csrf: true });
        }

        // First time we see a 401 / Unauthorized, try exactly ONE refresh + retry
        if (isUnauthorized && !retryState.auth) {
          const refreshed = await refreshSession();
          if (refreshed) {
            // Retry once with the same options
            return doFetch({ ...retryState, auth: true });
          }
          // Refresh failed – treat as logged out
          console.error(`[${new Date().toISOString()}] fetchWrapper: refresh failed after 401, logging out`);
          await logout();
          throw new Error('Unauthorized after failed refresh');
        }

        // If we’re here:
        //  - either it’s a second 401 after retry, or
        //  - some other non-OK status
        if (isUnauthorized) {
          console.error(`[${new Date().toISOString()}] fetchWrapper: 401/Unauthorized after retry, logging out`);
          await logout();
          throw new Error(errorText || 'Unauthorized');
        }

        // Non-401 error: bubble up with message
        throw new Error(errorText || `Request failed with status ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error(
        `[${new Date().toISOString()}] fetchWrapper error at ${url}:`,
        error?.message || error
      );
      // Network or other unexpected error – just rethrow
      throw error;
    }
  };

  // Start with no retry attempt
  return doFetch(false);
}, [user, fetchCsrfToken, refreshSession, logout]);

  const triggerWelcomeSuppliesEmail = useCallback(async () => {
    try {
      await fetchWrapper(`${process.env.REACT_APP_API_URL}/onboarding/welcome-supplies`, {
        method: 'POST'
      });
    } catch (error) {
      console.warn('[Onboarding] Welcome supplies email not sent:', error?.message || error);
    }
  }, [fetchWrapper]);

  // NEW HELPER: Retry wrapper for RPCs (lightweight, no CSRF)
  const rpcWithRetry = useCallback(async (rpcCall, maxRetries = 2, baseDelay = 300) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Pre-RPC lock: Get session and refresh if near expiry
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.expires_at * 1000 < Date.now() + 5 * 60 * 1000) { // 5min early
        if (!isRefreshCooldown()) {
          const refreshed = await refreshAndSync();
          if (refreshed?.data?.session) {
            console.log(`[Auth Lock] RPC pre-refresh succeeded for ${rpcCall.name || 'RPC'}`);
            setRefreshCooldown(15000);
          }
        } else {
          console.log('[Auth Lock] Skipping RPC pre-refresh (cooldown active)');
        }
      }
      console.log(`[AuthFlow] RPC attempt ${attempt + 1}/${maxRetries + 1} for ${rpcCall.name || 'RPC'}`);
      const result = await rpcCall();
      console.log(`[AuthFlow] RPC success on attempt ${attempt + 1}`);
      return result;
    } catch (error) {
      lastError = error;
      console.error(`[AuthFlow] RPC failed on attempt ${attempt + 1}:`, error.message);
      if (attempt < maxRetries && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
        await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt))); // Exponential backoff
      } else {
        throw lastError;
      }
    }
  }
}, [refreshAndSync, isRefreshCooldown, setRefreshCooldown]);

  // UPDATED: initializeMembership with optional delay for client settle
  const initializeMembership = useCallback(async (userId) => {
    if (!userId || fetching.current || isRegistering || isResettingPassword || oauthCompleteRef.current === false) {
      console.log(`[AuthFlow] Membership init SKIPPED (guards):`, { hasUser: !!userId, isFetching: fetching.current, oauthComplete: oauthCompleteRef.current });
      return;
    }
    fetching.current = true;
    setMembershipLoaded(false); // Reset flags
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] Membership init started for user: ${userId}`);
    try {
      // --- TEMPORARY BYPASS ---
      console.log(`[AuthContext] *** TEMPORARILY SKIPPING check_membership2 ***`);
      // Simulate a 'no_membership' response to let the function continue
      console.log(`[AuthContext] Calling check_membership2 RPC for user: ${userId}.`);
      const rpcCall = () => supabase
        .schema('clients')
        .rpc('check_membership2', { p_user_id: userId })
        .single();
      const { data: rpcData, error: rpcError } = await rpcWithRetry(() => rpcCall(), 2, 300); // NEW: Retry wrapper
      if (rpcError) {
        console.error(`[AuthContext] check_membership2 RPC Error:`, rpcError);
        throw rpcError;
      }
      console.log(`[AuthContext] check_membership2 SUCCESS. Data:`, rpcData);
      const finalStatus = rpcData.status;
      if (finalStatus === 'no_membership') {
        console.log(`[${new Date().toISOString()}] No membership row found (Simulated)`);
      }
      const has_active = (finalStatus === 'active' || finalStatus === 'cancelling');
      const is_cancelling = (finalStatus === 'cancelling');
      const is_expired_or_cancelled = (finalStatus === 'expired' || finalStatus === 'cancelled');
      setMembership({
        status: finalStatus,
        end_date: rpcData.end_date,
        tier: rpcData.tier,
        total_images_avail: rpcData.total_images_avail,
        has_active_membership: has_active,
        is_cancelling: is_cancelling,
        is_expired: is_expired_or_cancelled,
      });
      console.log(`[${new Date().toISOString()}] Membership object set (Simulated).`);
      // --- End Processing ---
      // --- NEW: Short delay for client auth prop (post-setSession settle) ---
      console.log(`[AuthContext] Delaying 100ms before select_profile for client state settle.`);
      await new Promise(resolve => setTimeout(resolve, 100));
      // --- END NEW ---
      // --- Profile Fetch (Keep this active) ---
      // LOG 3
      console.log(`[AuthContext] About to call select_profile.`);
      const profRpcCall = () => supabase
        .schema('clients')
        .rpc('select_profile', { p_client_id: userId });
      const { data: profData, error: profileError } = await rpcWithRetry(() => profRpcCall(), 1, 200); // NEW: Lighter retry for profile
      // LOG 4
      console.log(`[AuthContext] select_profile call FINISHED.`);
      if (profileError) { 
        console.error(`[AuthContext] select_profile RPC Error:`, profileError); 
        throw profileError; 
      }
      console.log(`[AuthContext] select_profile SUCCESS.`);
      setProfile(profData?.[0] || null);
      setIsAdmin(profData?.[0]?.is_admin || false);
      // LOG 5
      console.log(`[AuthContext] State updates complete (profile, admin).`);
      // --- End Profile Fetch ---
    } catch (error) {
      // LOG 6 (Error)
      console.error(`[${new Date().toISOString()}] Membership init CATCH block error:`, error.message);
      setMembership({ status: 'no_membership', has_active_membership: false, is_cancelling: false, is_expired: false });
      setProfile(null);
      setIsAdmin(false);
    } finally {
      setMembershipLoaded(true); // Mark membership check as done
      fetching.current = false;
      // LOG 7 (Finally)
      console.log(`[${new Date().toISOString()}] Membership init FINALLY block. MembershipLoaded: true`);
    }
  }, [isRegistering, isResettingPassword, supabase, rpcWithRetry]); // UPDATED DEPS: Add rpcWithRetry

  // Lean checkAuthStatus: No membership logic
const checkAuthStatus = useCallback(async () => {
  const timestamp = new Date().toISOString();
  if (isCheckingAuth.current || isResettingPassword) {
    console.log(`${timestamp} - Skipping checkAuthStatus, already in progress or during reset`);
    return;
  }
  console.log(`${timestamp} - Client-side checkAuthStatus called, attempting getSession`);
  isCheckingAuth.current = true;
  try {
    let { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error(`${timestamp} - Client-side getSession failed:`, error.message);
      throw new Error('Auth status check failed: ' + error.message);
    }
    // Proactive refresh if near expiry
    if (session?.expires_at * 1000 < Date.now() + 10 * 60 * 1000) {
  if (!isRefreshCooldown()) {
    const refreshed = await refreshAndSync();
    if (refreshed?.data?.session) {
      session = refreshed.data.session;
      console.log(`${timestamp} - [Auth Lock] checkAuthStatus refreshed session proactively`);
      setRefreshCooldown(15000);
    } else {
      console.log(`${timestamp} - [Auth Lock] checkAuthStatus refresh failed, treating as logged out`);
      
      // >>> This is the "bump to welcome" logic <<<
      setUser(null);
      resetStates();
      lastUserIdRef.current = null;
      setLoading(false);
      setMembershipLoaded(true);
      navigate('/welcome');
      // Make sure we don't continue with a stale session
      return;
    }
  } else {
    console.log(`${timestamp} - [Auth Lock] Skipping checkAuthStatus refresh (cooldown active)`);
  }
}

    const newUser = session?.user || null;
    if (lastUserIdRef.current === newUser?.id) {
      return;
    }
    console.log(`${timestamp} - Auth status response:`, { isAuthenticated: !!session, userId: newUser?.id });
setUser(newUser);
lastUserIdRef.current = newUser?.id || null;
if (!newUser) {
  resetStates();
}
 } catch (e) {
  console.error(`${timestamp} - Error in auth status check:`, e.message);
  if (e.message.includes('Already Used')) {
    if (!isRefreshCooldown()) {
      // Retry once with lock before reset
      const refreshed = await refreshAndSync();
      if (refreshed?.data?.session) {
        const newUser = refreshed.data.session.user || null;
        setUser(newUser);
        lastUserIdRef.current = newUser?.id || null;
        console.log(`${timestamp} - [Auth Lock] Caught 'Already Used' and refreshed in checkAuthStatus`);
        setRefreshCooldown(15000);
        return; // Skip reset
      }
    } else {
      console.log(`${timestamp} - [Auth Lock] Already Used during cooldown, skipping refresh`);
      return;
    }
  }
  if (isFallbacking.current) return; // Guard loop
  isFallbacking.current = true;
  setUser(null);
  resetStates();
  lastUserIdRef.current = null;
  setLoading(false);
  setMembershipLoaded(true);
  // Route to welcome on timeout (unblocks)
  navigate('/welcome');
  isFallbacking.current = false; // Reset guard
} finally {
  isCheckingAuth.current = false;
  setLoading(false);
}
}, [isResettingPassword]);

  // *** ALL useEffect HOOKS DECLARED HERE - AFTER ALL useCallback ***
  useEffect(() => {
  csrfLoadedRef.current = csrfLoaded;
}, [csrfLoaded]);
  useEffect(() => {
    const handleMembershipUpdated = async () => {
      // Restore the guard to include membershipInitializedRef.current
      if (!user?.id || fetching.current || !membershipInitializedRef.current) {
        console.log(`[${new Date().toISOString()}] 'membershipUpdated' event SKIPPED (Initial load or already fetching):`, {
          hasUser: !!user?.id,
          isFetching: fetching.current,
          isInitialized: membershipInitializedRef.current
        });
        return;
      }
      console.log(`[${new Date().toISOString()}] 'membershipUpdated' event: Re-running initializeMembership for user ${user.id}`);
      await initializeMembership(user.id);
    };
    window.addEventListener('membershipUpdated', handleMembershipUpdated);
    // Cleanup function
    return () => {
      window.removeEventListener('membershipUpdated', handleMembershipUpdated);
    };
  }, [user?.id, initializeMembership]); // Dependency array


// Shared init helper (define first)
const runMembershipInit = useCallback(async (userId, provider = 'email') => {
  if (!userId || membershipInitializedRef.current) return;
  console.log(`[AuthFlow] Running initializeMembership for ${provider} sign-in`);
  membershipInitializedRef.current = true;
  setMembershipInitialized(true);
  setTimeout(async () => await initializeMembership(userId), 0); // Microtask for React settle
}, [initializeMembership, setMembershipInitialized]);

// Plain email handler (define next)
const handleEmailSignIn = useCallback(async (session, newUser) => {
  const emailProvider = session.user?.identities?.[0]?.provider || 'email';
  console.log(`[${new Date().toISOString()}] ${emailProvider} Sign-In: Skipping token post (server cookies already set).`);
  oauthCompleteRef.current = true;
  setUser(newUser); // Explicit: Sync user state immediately for SignInCheck
  await syncServerSession();
  await runMembershipInit(newUser?.id, 'email');
}, [runMembershipInit, setUser, syncServerSession]);

// OAuth/linked handler (define last, before useEffect)
const handleOAuthSignIn = useCallback(async (session, newUser, provider) => {
  const accessToken = session?.access_token || '';
  const refreshToken = session?.refresh_token || '';
  if (!accessToken || !refreshToken) {
    console.error(`[${new Date().toISOString()}] ${provider} set-session error: Missing OAuth tokens in session`);
    return;
  }

  // Prevent duplicate /auth/set-session posts for the same exact auth session token.
  if (oauthSetSessionSyncedTokenRef.current === accessToken) {
    console.log('[AuthFlow] OAuth token already synced; skipping duplicate set-session call');
    oauthCompleteRef.current = true;
    setUser(newUser);
    await runMembershipInit(newUser?.id, provider);
    return;
  }

  if (
    oauthSetSessionInFlightRef.current &&
    oauthSetSessionInFlightTokenRef.current === accessToken
  ) {
    console.log('[AuthFlow] Awaiting in-flight OAuth session sync');
    try {
      await oauthSetSessionInFlightRef.current;
      oauthCompleteRef.current = true;
      setUser(newUser);
      await runMembershipInit(newUser?.id, provider);
    } catch (err) {
      console.error(`[${new Date().toISOString()}] ${provider} set-session error:`, err.message);
    }
    return;
  }

  console.log(`[${new Date().toISOString()}] ${provider.toUpperCase()} OAuth: Posting tokens to /auth/set-session`);
  const inFlight = (async () => {
    console.log(`[AuthFlow] OAuth POST start: ${new Date().toISOString()}`);
    const setRes = await fetch(`${process.env.REACT_APP_API_URL}/auth/set-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        access_token: accessToken,
        refresh_token: refreshToken,
      }),
    });
    if (!setRes.ok) {
      const errText = await setRes.text();
      throw new Error(`Set-session failed: ${setRes.status} - ${errText}`);
    }
    console.log(`[${new Date().toISOString()}] Server session set successfully`);
    console.log(`[AuthFlow] OAuth POST end: ${new Date().toISOString()}`);

    // Verify status (with retry)
    console.log(`[AuthFlow] Status verify start: ${new Date().toISOString()}`);
    let statusRes = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, { credentials: 'include' });
    const statusData = await statusRes.json();
    console.log(`[AuthFlow] Status verify response:`, { ok: statusRes.ok, isAuthenticated: statusData?.isAuthenticated });
    if (!statusRes.ok) {
      console.warn(`[${new Date().toISOString()}] Status verify failed post-set:`, statusRes?.status || 'unknown');
      await new Promise(resolve => setTimeout(resolve, 1000));
      const retryStatus = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, { credentials: 'include' });
      if (!retryStatus.ok) throw new Error('Status verify failed after retry');
    }
    console.log(`[${new Date().toISOString()}] Server status verified: Authenticated`);
    setRefreshCooldown(15000);

    // Prefetch CSRF
    console.log(`[AuthFlow] CSRF prefetch start: ${new Date().toISOString()}`);
    await fetchCsrfToken();
    console.log(`[AuthFlow] CSRF prefetch complete: ${new Date().toISOString()}`);
    queueMicrotask(() => {
      setCsrfToken(csrfTokenRef.current);
    });
    await new Promise(resolve => setTimeout(resolve, 500)); // Cookie lag for Render
  })();

  oauthSetSessionInFlightRef.current = inFlight;
  oauthSetSessionInFlightTokenRef.current = accessToken;

  try {
    await inFlight;
    oauthSetSessionSyncedTokenRef.current = accessToken;
    console.log(`[AuthFlow] OAuth flow complete: ${new Date().toISOString()}`);
    oauthCompleteRef.current = true;
    setUser(newUser); // Explicit: Sync user post-verify
    await runMembershipInit(newUser?.id, provider);
  } catch (err) {
    console.error(`[${new Date().toISOString()}] ${provider} set-session error:`, err.message);
    oauthCompleteRef.current = false;
    setUser(newUser); // Fallback: Set user anyway
    // Unblock UI on verify fail (force loaded, no stall)
    setLoading(false);
    setMembershipLoaded(true);
    resetStates(); // Clear flags
    // Route to welcome/signin (unblocks, no "Securing session..." loop)
    navigate(provider === 'google' ? '/welcome' : '/signin');
    // Fallback init delay (for if re-try)
    setTimeout(() => runMembershipInit(newUser?.id, provider), 500);
  } finally {
    if (oauthSetSessionInFlightRef.current === inFlight) {
      oauthSetSessionInFlightRef.current = null;
      oauthSetSessionInFlightTokenRef.current = null;
    }
  }
}, [fetchCsrfToken, runMembershipInit, setUser, navigate, setRefreshCooldown]); // Add deps

// Now the useEffect (depends on handlers above)
  const hasActiveMembership = useMemo(() => {
    return membership?.has_active_membership || false;
  }, [membership]);

  const ready = useMemo(() => !loading && (user ? membershipLoaded : true), [loading, user, membershipLoaded]);

  useEffect(() => {
    checkAuthStatus(); // Initial call on mount

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (authEvent, session) => {
      console.log(`Auth state change event: ${authEvent}`);
      const newUser = session?.user ?? null;

      // Temp debug: dump identities
      if (authEvent === 'SIGNED_IN') {
        console.log('[DEBUG] SIGNED_IN Session Guts:', {
          identities: session?.user?.identities,
          provider: session?.user?.identities?.[0]?.provider,
          email: session?.user?.email,
          hasIdentities: !!session?.user?.identities?.length,
        });
      }

      // SIGNED_IN: Decide between OAuth and plain email / magic link
      if (authEvent === 'SIGNED_IN' && session) {
        const appProvider = session.user?.app_metadata?.provider || 'email';
        const oauthIdentity = session.user?.identities?.find(
          (id) => id.provider !== 'email'
        );
        const resolvedProvider = appProvider || oauthIdentity?.provider || 'email';
        console.log('[Auth] SIGNED_IN provider resolution:', {
          appProvider,
          oauthIdentity: oauthIdentity?.provider || null,
          resolvedProvider,
        });

        if (resolvedProvider !== 'email') {
          handleOAuthSignIn(session, newUser, resolvedProvider);
        } else {
          // Plain email (single 'email' identity or none)
          handleEmailSignIn(session, newUser);
        }
      }

      // INITIAL_SESSION should hydrate app state immediately, regardless of provider.
      if (authEvent === 'INITIAL_SESSION' && newUser) {
        const initialProvider = session?.user?.app_metadata?.provider || 'email';
        const isSameUser = lastUserIdRef.current === newUser.id;
        console.log(`[Auth] INITIAL_SESSION (${initialProvider}) - hydrating and initializing membership`, { isSameUser });
        oauthCompleteRef.current = true;
        if (!isSameUser) {
          setUser(newUser);
          lastUserIdRef.current = newUser.id;
        }
        await syncServerSession();
        runMembershipInit(newUser.id, `initial_${initialProvider}`);
      }

      // Proactive expiry check
      if (
        (authEvent === 'TOKEN_REFRESHED' ||
          authEvent === 'SIGNED_IN') &&
        session?.expires_at * 1000 < Date.now() + 10 * 60 * 1000
      ) {
        if (!isRefreshCooldown()) {
          const refreshed = await refreshAndSync();
          if (refreshed?.data?.session) {
            setUser(refreshed.data.session.user); // Sync without broadcast
            console.log('[Auth Lock] Proactive refresh on event, session synced');
            setRefreshCooldown(15000);
          }
        } else {
          console.log('[Auth Lock] Skipping proactive refresh (cooldown active)');
        }
      }
    });

    return () => subscription.unsubscribe(); // Cleanup
  }, [checkAuthStatus, handleEmailSignIn, handleOAuthSignIn, runMembershipInit, refreshAndSync, isRefreshCooldown, setRefreshCooldown, syncServerSession]);

  useEffect(() => {
    if (!user?.id || isRegistering || isResettingPassword || !ready) {
      return;
    }
    if (welcomeEmailTriggeredRef.current === user.id) {
      return;
    }
    const run = async () => {
      if (!csrfLoadedRef.current) {
        await fetchCsrfToken();
      }
      if (!csrfLoadedRef.current) {
        console.warn('[Onboarding] CSRF token unavailable, deferring welcome email.');
        return;
      }
      welcomeEmailTriggeredRef.current = user.id;
      console.log('[Onboarding] Triggering welcome supplies email for user:', user.id);
      await triggerWelcomeSuppliesEmail();
    };
    run();
  }, [user?.id, isRegistering, isResettingPassword, ready, fetchCsrfToken, triggerWelcomeSuppliesEmail]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get('success') || query.get('sessionId')) {
      checkAuthStatus();
    }
  }, [checkAuthStatus]);
  // Idle poll for proactive refresh every 5min
 useEffect(() => {
  if (!user) return;
  const interval = setInterval(() => {
    checkAuthStatus().catch(e => {
      console.error('Poll error:', e.message);
      // Fallback unblock (catches async bubble)
      setLoading(false);
      setMembershipLoaded(true);
      resetStates();
      navigate('/welcome');
    });
  }, 5 * 60 * 1000); // 5min
  return () => clearInterval(interval);
}, [user, checkAuthStatus, navigate]);
  // UPDATED: Expose new flags from membership + isInitializing for UI
  const value = useMemo(() => ({
    user, membership, profile, isAdmin, loading, csrfToken,
    fetchCsrfToken, checkAuthStatus, logout, setIsRegistering, refreshSession, supabase,
    isResettingPassword, setIsResettingPassword, fetchWrapper,
    membershipLoaded, ready, hasActiveMembership, initializeMembership,
    csrfLoaded, 
    // NEW: Expose for routing/UI (from membership)
    membershipStatus: membership?.status || 'no_membership',
    isExpired: membership?.is_expired || false,
    isCancelling: membership?.is_cancelling || false,
    // NEW: For spinners in SignInCheck/UserDashboard
    isInitializing: fetching.current || !oauthCompleteRef.current,
    // REMOVED: balance, isCancelled (use new flags)
  }), [
    user, membership, profile, isAdmin, loading, csrfToken,
    fetchCsrfToken, checkAuthStatus, logout, refreshSession, supabase,
    isResettingPassword, fetchWrapper, membershipLoaded, ready, hasActiveMembership,
    // NEW DEPS: For exposed flags
    membership?.status, membership?.is_expired, membership?.is_cancelling, initializeMembership,
    fetching.current, rpcWithRetry, oauthCompleteRef.current // NEW: For isInitializing
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
